/* ==========================================================================
   DIANA QUINN — Sistema de filtros por colección
   --------------------------------------------------------------------------
   Lógica: OR dentro de un mismo grupo (ej. Forma=arracada O huggie),
           AND entre grupos distintos (ej. Forma=arracada Y Acabado=circonia).
   Fuente de datos: JSON embebido en la sección (todo product.tags en minúsculas).
   Colecciones grandes (>250 productos, el máximo que Liquid puede paginar de
   una sola vez) se completan en segundo plano pidiendo las páginas siguientes
   vía la API de secciones de Shopify (?section_id=...), sin recargar la página.
   ========================================================================== */
(function () {
  'use strict';

  if (customElements.get('dq-collection-filters')) return;

  class DQCollectionFilters extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.collectionUrl = this.dataset.collectionUrl;
      this.totalPages = parseInt(this.dataset.totalPages || '1', 10);

      this.grid = this.querySelector('[data-dq-grid]');
      this.emptyState = this.querySelector('[data-dq-empty]');
      this.resultsCountEl = this.querySelector('[data-dq-count]');
      this.footerCountEl = this.querySelector('[data-dq-count-footer]');
      this.clearBtns = Array.from(this.querySelectorAll('[data-dq-clear]'));
      this.loadingMoreEl = this.querySelector('[data-dq-loading-more]');
      this.badgeEl = this.querySelector('[data-dq-open-badge]');
      this.activeChipsWrap = this.querySelector('[data-dq-active-chips]');

      this.drawerToggle = this.querySelector('[data-dq-drawer-toggle]');
      this.drawerPanel = this.querySelector('[data-dq-drawer]');
      this.drawerOverlay = this.querySelector('[data-dq-overlay]');
      this.drawerClose = this.querySelector('[data-dq-drawer-close]');
      this.drawerApply = this.querySelector('[data-dq-drawer-apply]');

      this.groups = this._readJSON('[data-dq-groups]') || [];
      this.products = this._readJSON('[data-dq-products]') || [];

      this._bindEvents();
      this._applyFromURL();
      this._loadRemainingPages();
    }

    _readJSON(selector) {
      const el = this.querySelector(selector);
      if (!el) return null;
      try {
        return JSON.parse(el.textContent);
      } catch (err) {
        console.error('DQ Filters: JSON inválido en', selector, err);
        return null;
      }
    }

    /* ---------------------------------------------------------------- */
    /* Carga progresiva para colecciones con más de 250 productos       */
    /* ---------------------------------------------------------------- */
    async _loadRemainingPages() {
      if (this.totalPages <= 1) return;
      if (this.loadingMoreEl) this.loadingMoreEl.hidden = false;

      for (let page = 2; page <= this.totalPages; page++) {
        try {
          const url = `${this.collectionUrl}?page=${page}&section_id=${this.sectionId}`;
          const res = await fetch(url);
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const html = await res.text();
          const doc = new DOMParser().parseFromString(html, 'text/html');

          const extraProducts = doc.querySelector('[data-dq-products]');
          if (extraProducts) {
            try {
              const parsed = JSON.parse(extraProducts.textContent) || [];
              this.products = this.products.concat(parsed);
            } catch (e) {
              /* ignore malformed page */
            }
          }

          const extraGrid = doc.querySelector('[data-dq-grid]');
          if (extraGrid && this.grid) {
            Array.from(extraGrid.children).forEach((li) => {
              const id = li.getAttribute('data-product-id');
              if (id && !this.grid.querySelector(`[data-product-id="${id}"]`)) {
                this.grid.appendChild(li);
              }
            });
          }
        } catch (err) {
          console.error('DQ Filters: no se pudieron cargar más productos (página ' + page + ')', err);
          break;
        }
      }

      if (this.loadingMoreEl) this.loadingMoreEl.hidden = true;
      // Con el catálogo completo ya en memoria, recalculamos todo.
      this._applyFromURL();
    }

    /* ---------------------------------------------------------------- */
    /* Eventos                                                          */
    /* ---------------------------------------------------------------- */
    _bindEvents() {
      // Las opciones son enlaces <a> reales (funcionan sin JS via el filtro
      // nativo de Shopify por segmentos de URL con '+'). Con JS, interceptamos
      // el click y aplicamos nuestra propia lógica OR-dentro-de-grupo /
      // AND-entre-grupos en el cliente, sin recargar la página.
      this.addEventListener('click', (event) => {
        const link = event.target.closest('[data-dq-option]');
        if (link) {
          event.preventDefault();
          link.classList.toggle('is-active');
          link.setAttribute('aria-pressed', link.classList.contains('is-active') ? 'true' : 'false');
          this._syncFromInputs();
        }
      });

      this.clearBtns.forEach((btn) => {
        btn.addEventListener('click', () => this._clearAll());
      });

      if (this.drawerToggle) {
        this.drawerToggle.addEventListener('click', () => this._openDrawer());
      }
      if (this.drawerOverlay) {
        this.drawerOverlay.addEventListener('click', () => this._closeDrawer());
      }
      if (this.drawerClose) {
        this.drawerClose.addEventListener('click', () => this._closeDrawer());
      }
      if (this.drawerApply) {
        this.drawerApply.addEventListener('click', () => this._closeDrawer());
      }

      window.addEventListener('popstate', () => this._applyFromURL());

      const sortSelect = this.querySelector('[data-dq-sort]');
      if (sortSelect) {
        sortSelect.addEventListener('change', () => {
          const params = new URLSearchParams(window.location.search);
          params.set('sort_by', sortSelect.value);
          // La ordenación es nativa de Shopify (server-side): recargamos.
          // Los filtros se re-aplican solos al leer la URL en el próximo load.
          window.location.search = params.toString();
        });
      }
    }

    _openDrawer() {
      if (this.drawerPanel) this.drawerPanel.classList.add('is-open');
      if (this.drawerOverlay) this.drawerOverlay.classList.add('is-open');
      document.body.classList.add('dq-drawer-open');
    }

    _closeDrawer() {
      if (this.drawerPanel) this.drawerPanel.classList.remove('is-open');
      if (this.drawerOverlay) this.drawerOverlay.classList.remove('is-open');
      document.body.classList.remove('dq-drawer-open');
    }

    /* ---------------------------------------------------------------- */
    /* Estado / URL                                                     */
    /* ---------------------------------------------------------------- */
    _getStateFromInputs() {
      const state = {};
      this.querySelectorAll('[data-dq-option].is-active').forEach((link) => {
        const group = link.dataset.group;
        state[group] = state[group] || new Set();
        state[group].add(link.dataset.value);
      });
      return state;
    }

    _syncFromInputs() {
      const state = this._getStateFromInputs();
      this._writeURL(state);
      this._render(state);
    }

    _writeURL(state) {
      const params = new URLSearchParams();
      Object.keys(state).forEach((group) => {
        const values = Array.from(state[group]);
        if (values.length) params.set(group, values.join(','));
      });
      const qs = params.toString();
      const newUrl = `${window.location.pathname}${qs ? '?' + qs : ''}`;
      history.pushState({ dqFilters: qs }, '', newUrl);
    }

    _applyFromURL() {
      const params = new URLSearchParams(window.location.search);
      const validKeys = new Set(this.groups.map((g) => g.key));
      const state = {};
      params.forEach((value, key) => {
        if (validKeys.has(key) && value) {
          state[key] = new Set(value.split(',').filter(Boolean));
        }
      });

      this.querySelectorAll('[data-dq-option]').forEach((link) => {
        const group = link.dataset.group;
        const active = !!(state[group] && state[group].has(link.dataset.value));
        link.classList.toggle('is-active', active);
        link.setAttribute('aria-pressed', active ? 'true' : 'false');
      });

      this._render(state);
    }

    _clearAll() {
      this.querySelectorAll('[data-dq-option].is-active').forEach((link) => {
        link.classList.remove('is-active');
        link.setAttribute('aria-pressed', 'false');
      });
      this._writeURL({});
      this._render({});
    }

    /* ---------------------------------------------------------------- */
    /* Filtrado: OR dentro del grupo, AND entre grupos                  */
    /* ---------------------------------------------------------------- */
    _productMatches(productTags, state, ignoreGroup) {
      return Object.keys(state).every((group) => {
        if (group === ignoreGroup) return true;
        const values = state[group];
        if (!values || !values.size) return true;
        for (const v of values) {
          if (productTags.indexOf(v) !== -1) return true;
        }
        return false;
      });
    }

    _render(state) {
      const hasActiveFilters = Object.values(state).some((s) => s.size);
      let visibleCount = 0;
      const visibleIds = new Set();

      this.products.forEach((product) => {
        const tags = product.tags || [];
        if (this._productMatches(tags, state, null)) {
          visibleCount += 1;
          visibleIds.add(String(product.id));
        }
      });

      if (this.grid) {
        Array.from(this.grid.children).forEach((li) => {
          const id = li.getAttribute('data-product-id');
          li.hidden = hasActiveFilters && !visibleIds.has(id);
        });
        this.grid.hidden = visibleCount === 0;
      }

      if (this.emptyState) this.emptyState.hidden = visibleCount !== 0;

      if (this.resultsCountEl) {
        this.resultsCountEl.textContent =
          visibleCount === 1 ? '1 producto' : `${visibleCount} productos`;
      }
      if (this.footerCountEl) this.footerCountEl.textContent = String(visibleCount);

      this.clearBtns.forEach((btn) => {
        // El botón dentro del estado vacío siempre debe poder limpiar;
        // el de la barra superior sólo se muestra si hay filtros activos.
        if (btn.closest('[data-dq-empty]')) return;
        btn.hidden = !hasActiveFilters;
      });
      if (this.badgeEl) {
        const totalSelected = Object.values(state).reduce((sum, s) => sum + s.size, 0);
        this.badgeEl.textContent = totalSelected > 0 ? String(totalSelected) : '';
      }

      this._updateCounts(state);
      this._updateActiveChips(state);
    }

    // Conteo "tipo facet": cuenta cuántos productos tendría cada opción si se
    // agregara, respetando los filtros ya activos en LOS DEMÁS grupos (para
    // que el usuario vea cuántos resultados sumaría cada casilla adicional).
    _updateCounts(state) {
      this.groups.forEach((group) => {
        group.options.forEach((option) => {
          let count = 0;
          this.products.forEach((product) => {
            const tags = product.tags || [];
            if (tags.indexOf(option.value) === -1) return;
            if (this._productMatches(tags, state, group.key)) count += 1;
          });

          const countEl = this.querySelector(
            `[data-dq-count-for="${group.key}::${CSS.escape(option.value)}"]`
          );
          if (countEl) countEl.textContent = `(${count})`;

          const isChecked = !!(state[group.key] && state[group.key].has(option.value));
          const optWrap = this.querySelector(
            `[data-dq-option-wrap="${group.key}::${CSS.escape(option.value)}"]`
          );
          if (optWrap) optWrap.classList.toggle('dq-option--zero', count === 0 && !isChecked);
        });
      });
    }

    _updateActiveChips(state) {
      if (!this.activeChipsWrap) return;
      this.activeChipsWrap.innerHTML = '';

      Object.keys(state).forEach((groupKey) => {
        const group = this.groups.find((g) => g.key === groupKey);
        if (!group) return;
        state[groupKey].forEach((value) => {
          const option = group.options.find((o) => o.value === value);
          const label = option ? option.label : value;

          const chip = document.createElement('button');
          chip.type = 'button';
          chip.className = 'dq-chip';
          chip.setAttribute('aria-label', `Quitar filtro ${label}`);
          chip.textContent = `${label} ×`;
          chip.addEventListener('click', () => {
            const link = this.querySelector(
              `[data-dq-option][data-group="${groupKey}"][data-value="${CSS.escape(value)}"]`
            );
            if (link) {
              link.classList.remove('is-active');
              link.setAttribute('aria-pressed', 'false');
              this._syncFromInputs();
            }
          });
          this.activeChipsWrap.appendChild(chip);
        });
      });
    }
  }

  customElements.define('dq-collection-filters', DQCollectionFilters);
})();
